import { Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import { Observable, of, Subject } from 'rxjs';
import { filter, map, mergeMap, takeUntil, tap } from 'rxjs/operators';
import { Sortable } from 'src/app/core/classes/sortable';
import {
  STATUS,
  PAYMENT,
  ORDER_TYPE,
  PLATFORM,
  ORDER_STATUS_BADGE,
  PAYMENT_STATUS_BADGE,
  ORDER_STATUS_HINT,
  PAYMENT_STATUS_HINT,
} from 'src/app/core/constants/data.constant';
import { StoreState } from 'src/app/core/states/store.state';
import { environment } from 'src/environments/environment';
import * as moment from 'moment';
import {
  GetOrdersInfo,
  StoreNotLoading,
} from 'src/app/core/states/store.actions';
import { NgbDate, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { StoreService } from 'src/app/store.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ModalDismissReasons } from '@ng-bootstrap/ng-bootstrap';
import {
  animate,
  AUTO_STYLE,
  query,
  stagger,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { StoreInfoService } from 'src/app/store-info.service';
import { UtilitiesService } from 'src/app/core/utilities.service';
import { DEALER_STATUS } from 'src/app/core/constants/data.constant';

@Component({
  selector: 'orders-history',
  templateUrl: './orders.component.html',
  styleUrls: ['./orders.component.scss'],
  animations: [
    trigger('listAnimation', [
      transition('* => *', [
        // each time the binding value changes
        query(
          ':leave',
          [stagger(100, [animate('0.5s', style({ opacity: 0 }))])],
          { optional: true }
        ),
        query(
          ':enter',
          [
            style({ opacity: 0 }),
            stagger(100, [animate('0.5s', style({ opacity: 1 }))]),
          ],
          { optional: true }
        ),
      ]),
    ]),
  ],
})
export class OrdersComponent extends Sortable implements OnInit, OnDestroy {
  @Input() embedded = false;
  tableDataStructure = [
    { id: 'id', type: 'number' },
    { id: 'createDate', type: 'date' },
    { id: 'status', type: 'text' },
    { id: 'total', type: 'number' },
  ];

  DATE_FILTERS = [
    { display: 'Today', value: 'today', range: false, selectable: false },
    { display: 'Yesterday', value: 'yesterday', range: false, selectable: false },
    { display: 'Current Month', value: 'cm', range: false, selectable: false },
    { display: 'Last Month', value: 'lm', range: false, selectable: false },
    { display: 'Last 30 Days', value: 'last30', range: false, selectable: false },
  ];

  sortList = [
    { text: 'Order No.', value: 'id' },
    { text: 'Create Date', value: 'createDate' },
    { text: 'Payment Status', value: 'status' },
    { text: 'Total Amount', value: 'total' },
  ];

  statusCodFilters = ['Approve', 'Reject'];
  selectedCodStatus = [];
  selectedShowAll = [];
  selectedShowAllText = [];
  remark = '';
  remarkError = false;
  currentClass = '';

  filterTable = [
    {
      filterName: 'selectedStatus',
      filterCallback: (orderData, filterValue) =>
        (orderData?.order_status_label ?? orderData?.status) === filterValue,
    },
    {
      filterName: 'selectedPaymentStatus',
      filterCallback: (orderData, filterValue) =>
        orderData?.payment_status_label === filterValue,
    },
    {
      filterName: 'selectedPaymentType',
      filterCallback: (orderData, filterValue) =>
        orderData?.payment_type === filterValue,
    },
    {
      filterName: 'selectedOrderType',
      filterCallback: (orderData, filterValue) => {
        if (filterValue === 'normal') {
          return ![2, 3, 4, 5, 7].includes(+orderData.order_type);
        } else if (filterValue === 'express_checkout') {
          return +orderData.order_type === 2;
        } else if (filterValue === 'aemis') {
          return +orderData.order_type === 3;
        } else if (filterValue === 'subscription') {
          return +orderData.order_type === 4;
        } else if (filterValue === 'pre_order') {
          return +orderData.order_type === 5;
        } else if (filterValue === 'repurchase') {
          return +orderData.order_type === 7;
        }
        return false;
      },
    },
    {
      filterName: 'selectedOrderStatus',
      filterCallback: (orderData, filterValue) => {
        if (filterValue === 'exp') {
          return +orderData.checkout_link_id !== 0;
        } else if (filterValue === 'non_exp') {
          return +orderData.checkout_link_id === 0;
        }
        return false;
      },
    },
    {
      filterName: 'selectedPlatformType',
      filterCallback: (orderData, filterValue) =>
        orderData?.platform === filterValue,
    },
    {
      filterName: 'selectedPendingOrder',
      filterCallback: (orderData, filterValue) => {
        if (filterValue === 'pendingOrder') {
          return ['COD APPROVAL', 'SELF COLLECT'].includes(orderData.status);
        }
      },
    },
    {
      filterName: 'selectedExpressCheckoutLink',
      filterCallback: (orderData, filterValue) => {
        if (!filterValue || filterValue === 'ALL') {
          return true;
        }
        if (filterValue === 'EXPRESS_ALL') {
          const hasLinkId =
            orderData?.checkout_link_id && +orderData.checkout_link_id !== 0;
          const hasLinkUrl =
            orderData?.checkout_link_url && orderData.checkout_link_url !== '';
          const hasLinkDetails =
            orderData?.checkout_link_details &&
            !this.emptyCheckoutLinkDetails(orderData.checkout_link_details);
          const isExpOrderType =
            +orderData?.order_type === 2 || +orderData?.order_type === 3;
          return hasLinkId || hasLinkUrl || hasLinkDetails || isExpOrderType;
        }
        const valStr = String(filterValue).toLowerCase().trim();

        const orderLinkId = String(
          orderData?.checkout_link_id || orderData?.checkout_link_details?.id || ''
        )
          .toLowerCase()
          .trim();
        const orderLinkUrl = String(
          orderData?.checkout_link_url ||
            orderData?.checkout_link_details?.url ||
            orderData?.checkout_link_details?.slug ||
            ''
        )
          .toLowerCase()
          .trim();
        const orderLinkTitle = String(
          orderData?.checkout_link_title ||
            orderData?.checkout_link_details?.title ||
            orderData?.checkout_link_details?.name ||
            ''
        )
          .toLowerCase()
          .trim();

        if (orderLinkId && orderLinkId !== '0' && orderLinkId === valStr) {
          return true;
        }
        if (
          orderLinkUrl &&
          (orderLinkUrl === valStr ||
            valStr.includes(orderLinkUrl) ||
            orderLinkUrl.includes(valStr))
        ) {
          return true;
        }
        if (
          orderLinkTitle &&
          (orderLinkTitle === valStr ||
            valStr.includes(orderLinkTitle) ||
            orderLinkTitle.includes(valStr))
        ) {
          return true;
        }
        return false;
      },
    },
  ];

  o_id;
  id;
  orderId;

  availableCheckoutLinks: any[] = [];
  selectedExpressCheckoutLink: any = { display: 'All Orders', value: 'ALL' };
  checkoutLinkSearchText: string = '';
  checkoutLinksLoading: boolean = false;

  withPagination = false;

  closeResult = '';

  selectedOrderDetailsTab = 0;

  orderList = [];
  allCustomers = [];
  storeId;
  store_url = '';
  successUrl = '';
  returnUrl = '';
  selectedStatus; //{ display:'All', value:'ALL' };
  selectedPaymentStatus;
  selectedCour; // = { display:'All', value:'ALL' };
  selectedPaymentType; // = { display:'All', value:'ALL' };
  selectedOrderType; // = { display:'All', value:'ALL' };
  selectedOrderStatus; // = { display:'All', value:'ALL' };
  selectedPlatformType; // = { display:'All', value:'ALL' };

  dataIsLoading = true;
  loading = false;
  isExporting = false;

  sortOrder = this.sortList[0];

  statusFilters = [];
  paymentStatusFilters = [];
  courierFilters = [];
  paymentTypeFilters = [];
  // orderTypeFilters = [];
  // orderStatusFilters = [];
  platformTypeFilters = [];

  orderTypeFilters = [
    { display: 'View All', value: 'ALL' },
    { display: 'Normal', value: 'normal' },
    { display: 'Express Checkout', value: 'express_checkout' },
    { display: 'Aemis', value: 'aemis' },
    { display: 'Subscription', value: 'subscription' },
    { display: 'Pre Order', value: 'pre_order' },
    { display: 'Repurchase', value: 'repurchase' },
  ];
  orderStatusFilters = [
    { display: 'View All', value: 'ALL' },
    { display: 'Normal Products', value: 'non_exp' },
    { display: 'Subscriptions', value: 'exp' },
  ];

  exportType = [
    // { display: 'Csv', value: 'csv' },
    { display: 'Excel', value: 'excel' },
    // { display: 'PDF',  value: 'pdf'},
  ];

  orders = {
    type: null,
    yesterday: [],
    today: [],
    cm: [],
    lm: [],
    cy: [],
    raw: [],
  };

  startDate;
  endDate;
  myDate = new Date();
  day;
  month;
  year;
  currentDate;
  minDate;
  maxDate;
  orderSearchDone = false;

  expanded = [];

  destroy$: Subject<boolean> = new Subject<boolean>();
  @Select(StoreState.orderInfo) orders$: Observable<any[]>;

  hoveredDate: NgbDate | null = null;
  fromDate: NgbDate;
  toDate: NgbDate | null = null;

  OrderHistoryList = [];
  orderPaginationList;
  OrderList;
  productDetail;
  bearer;
  sub;
  dateFilter: any;
  selectedOrderHistory: any;

  partnerPlatformUrl;
  codData: any = [];
  storeOrderList;
  dealerInfo: any = null;

  page = 1;
  pageSize = 20;
  start = 0;
  end = 0;
  limit = 1000;
  paginationPage = 1;
  paginationPageSize = 20;
  totalPage = 1;
  totalRecord = 0;
  paginationData;
  filteredOrderList;
  allOrderList;
  isReferralExist = false;
  orderReferralExist = false;
  storeInfo$ = this.storeInfoService.getStoreInfos();
  dealerStatus = '';
  pendingOrderDisplay: boolean = false;
  pendingOrderCloseBtn: boolean = false;
  pendingOrder = [];
  selectedPendingOrder;
  pendingOrderCounter = 0;
  orderReferralLinkExist = false;
  buttonLabel = '';

  constructor(
    private svc: StoreService,
    private store: Store,
    private action: Actions,
    private route: ActivatedRoute,
    private modalService: NgbModal,
    private location: Location,
    public utils: UtilitiesService,
    private storeInfoService: StoreInfoService
  ) {
    super();

    this.store_url = localStorage.getItem('store_url');

    this.dateFilter = this.DATE_FILTERS[2];

    this.route.params.subscribe((params) => {
      if (params['id'] > 0 && this.orderSearchDone) {
        this.id = params['id'];
        this.getSearchOrderHistory();
      }
      this.location.replaceState('/dashboard/orders');
    });

    this.action.pipe(ofActionDispatched(GetOrdersInfo)).subscribe((_) => {
      this.dataIsLoading = true;
      this.withPagination = false;
      this.orders = {
        ...this.orders,
        yesterday: [],
        today: [],
        cm: [],
        lm: [],
        cy: [],
        raw: [],
      };
    });
  }

  ngOnInit() {
    this.orderSearchDone = true;
    this.route.params.subscribe((params) => {
      if (params['id'] > 0) {
        this.id = params['id'];
      }
      this.location.replaceState('/dashboard/orders');
    });

    this.buttonLabel =
      environment.storeSubdomain.includes('estoreglobe') ||
      environment.storeSubdomain.includes('globemerce')
        ? 'GlobeMerce'
        : 'FIV5S';

    this.store
      .dispatch(new GetOrdersInfo())
      .pipe(
        mergeMap((_) => this.orders$),
        tap((_) => this.store.dispatch(new StoreNotLoading())),
        takeUntil(this.destroy$)
      )
      .subscribe(
        (_) => {
          const orders = this.store.selectSnapshot(StoreState.orderInfo);
          if (orders.length > 0) {
            const ordersList = orders.map((item) => {
              const status = item.paymentStatus.toUpperCase();
              const payment_type = item.paymentStatus.toUpperCase();
              return {
                ...item,
                status,
                payment_type,
                class: this.getStatus(status).class,
                cDate: item.createDate,
                createDate: moment
                  .unix(item.createTimestamp)
                  .format('MMM DD, YYYY hh:mm'),
                fiv5s_response: item.fiv5s_response,
              };
            });

            this.getFilterGroup();
            this.populateCheckoutLinksFromOrders();

            this.prepareOrders(this.orders, ordersList, (which, item) =>
              which.push(item)
            );
          }
          this.dataIsLoading = false;
        },
        (_) => {
          this.store.dispatch(new StoreNotLoading());
          this.dataIsLoading = false;
        }
      );

    this.day = this.myDate.getDate();
    this.month = this.myDate.getMonth(); // Be careful! January is 0, not 1
    this.year = this.myDate.getFullYear();
    this.currentDate = this.year + '-' + (this.month + 1) + '-' + this.day;
    this.minDate = { year: 2021, month: 1, day: 1 };
    this.maxDate = { year: this.year, month: this.month + 1, day: this.day };
    // this.startDate = this.endDate = this.currentDate;
    this.bearer = localStorage.getItem('bearer');
    this.storeId = localStorage.getItem('store_id');

    this.startDate = this.year + '-' + (this.month + 1) + '-01';
    this.endDate = this.currentDate;

    this.getStoreInfos();
    this.getProgramInfo(this.storeId);
    this.getDealerInfo();

    if (this.id) {
      this.getSearchOrderHistory();
    } else {
      // this.getOrderHistoryList();
      this.datePeriod();
    }
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  download(type) {
    if (this.isExporting) return;

    const filterValue = (filter) => filter?.value ?? 'ALL';

    // Same filters as filterTable, so the file holds what the dealer is looking at.
    // Every order in the date range is exported — no page/limit.
    const param: Record<string, string> = {
      store_id: this.storeId,
      status: filterValue(this.selectedStatus),
      paymentStatus: filterValue(this.selectedPaymentStatus),
      paymentType: filterValue(this.selectedPaymentType),
      platform: filterValue(this.selectedPlatformType),
      source: filterValue(this.selectedOrderType),
      orderStatus: filterValue(this.selectedOrderStatus),
      pendingOrder: filterValue(this.selectedPendingOrder),
      checkoutLink: filterValue(this.selectedExpressCheckoutLink),
      type: type,
    };

    // An order-id search lists that order regardless of date, so export it the same way.
    if (this.id) {
      param.o_id = this.id;
    } else {
      param.start_date = this.startDate;
      param.end_date = this.endDate;
    }

    this.isExporting = true;
    this.svc
      .downloadSpreadsheet(this.bearer, '/order/export_orders', param)
      .catch((err) => {
        console.error('[orders] export failed', err);
        this.utils.msgBox.open({
          message: this.utils.translate(
            'We could not export the orders. Please try again.'
          ),
          title: this.utils.translate('Export failed'),
          icon: 'error',
          buttonList: [{ text: this.utils.translate('OK') }],
        });
      })
      .then(() => (this.isExporting = false));
  }

  getStoreInfos() {
    this.storeInfo$.subscribe((res) => {
      this.partnerPlatformUrl = res?.config?.partner_platform_url;

      this.store_url = res.store_url;
      this.successUrl = encodeURIComponent(this.store_url + '/thank-you');
      this.returnUrl = encodeURIComponent(this.store_url + '/payment-failed');
    });
  }

  isSubsOrCOD(payment_status) {
    return [4, 7, 8].includes(+payment_status);
  }

  getOrderId(data) {
    if (data.o_id) {
      return data.o_id;
    } else {
      return data.store_order_id;
    }
  }

  cancelSubscription(id) {
    this.utils.msgBox.open({
      message: this.utils.translate(
        'Are you sure you want to cancel this subscription order?'
      ),
      title: this.utils.translate('Confirm Cancel?'),
      icon: 'warning',
      buttonList: [
        {
          text: this.utils.translate('No'),
        },
        {
          text: this.utils.translate('Yes'),
          style: 'danger',
          onClick: () => {
            this.loading = true;
            let body = {
              store_order_id: id,
            };
            this.svc.cancelSubscription(body, this.bearer).subscribe(
              (res) => {
                if (res['data'] && typeof res['data'] === 'string') {
                  this.utils.notify.showSuccess(res['data']);
                } else {
                  this.utils.notify.showSuccess(
                    this.utils.translate('Updated Successfully!')
                  );
                }
                this.loading = false;
                this.datePeriod();
              },
              (error) => {
                if (
                  error['error']['status_code'] === 400 ||
                  error['error']['status_code'] === 'error' ||
                  error['error']['status'] === false
                ) {
                  this.utils.notify.showError(
                    error['error']['message'] ?? error['message']
                  );
                }
              }
            );
          },
        },
      ],
    });
  }

  getOrderHistoryList() {
    this.orderId = '';
    this.dataIsLoading = true;
    this.OrderHistoryList = [];

    this.svc
      .getOrderHistoryList(
        this.bearer,
        this.page,
        this.limit,
        this.startDate,
        this.endDate
      )
      .subscribe(
        (res) => {
          this.OrderHistoryList = res['data']['data'];
          this.storeOrderList = this.OrderHistoryList;
          this.allOrderList = res['data']['data'];
          this.paginationData = res['data']['pagination'];
          this.page = this.paginationData.page;
          this.totalPage = this.paginationData.total_page;
          this.totalRecord = this.paginationData.total_record;

          if (this.OrderHistoryList && this.OrderHistoryList.length > 0) {
            this.OrderHistoryList.forEach((value, index) => {
              this.expanded[index] = false;
              const isPendingOrder =
                value?.status === 'COD APPROVAL' ||
                value?.status === 'SELF COLLECT';
              if (isPendingOrder) {
                this.pendingOrder.push(value);
              }
              value.status =
                value.payment_status === '8' &&
                value.payment_type === 'self_collect'
                  ? 'CANCEL SELF COLLECT'
                  : value.status;
            });
          }
          this.getFilterGroup();
          this.populateCheckoutLinksFromOrders();
          this.orderPaginationList = res['data']['pagination'];
          this.sliceRecord();
          this.dataIsLoading = false;
          this.id = '';
          if (!this.pendingOrderCloseBtn && !this.loading) {
            this.checkPendingOrder();
          }
          if (this.loading) {
            this.checkPendingOrder();
          }
        },
        (error) => {
          this.dataIsLoading = false;
        }
      );
  }

  populateCheckoutLinksFromOrders() {
    const map = new Map<
      string,
      { id: any; title: string; url: string; displayTitle: string }
    >();

    const sourceList = this.allOrderList || this.OrderHistoryList || [];
    if (Array.isArray(sourceList)) {
      sourceList.forEach((order) => {
        const hasLinkUrl =
          order?.checkout_link_url && order.checkout_link_url.trim() !== '';
        const hasLinkDetails =
          order?.checkout_link_details &&
          !this.emptyCheckoutLinkDetails(order.checkout_link_details);

        // ONLY include orders that actually have express checkout link details or URL
        if (hasLinkUrl || hasLinkDetails) {
          let title =
            order.checkout_link_title ||
            order.checkout_link_details?.title ||
            order.checkout_link_details?.name ||
            '';
          let url =
            order.checkout_link_url ||
            (order.checkout_link_details
              ? this.getCheckoutUrl(order.checkout_link_details)
              : '');

          if (!title && order.checkout_link_details?.url) {
            title = order.checkout_link_details.url;
          }

          // Skip if neither title nor URL exists
          if (!title && !url) return;

          const rawId =
            order.checkout_link_id ||
            order.checkout_link_details?.id ||
            url ||
            title;
          const key = String(rawId).trim();

          if (key && key !== '0' && !map.has(key)) {
            let displayTitle = '';
            if (title && url && title !== url) {
              const formattedTitle = title.startsWith('(') ? title : `(${title})`;
              displayTitle = `${formattedTitle} ${url}`;
            } else if (title) {
              displayTitle = title.startsWith('(') ? title : `(${title})`;
            } else {
              displayTitle = url;
            }

            map.set(key, {
              id: key,
              title: title || displayTitle,
              url: url,
              displayTitle: displayTitle,
            });
          }
        }
      });
    }

    this.availableCheckoutLinks = Array.from(map.values());
  }

  selectExpressCheckoutFilter(filterObj: { display: string; title?: string; value: any }) {
    this.selectedExpressCheckoutLink = filterObj;
    this.paginationPage = 1;
    this.filteredOrderHistoryList;
  }

  getFilteredCheckoutLinkOptions() {
    if (!this.availableCheckoutLinks) return [];
    if (!this.checkoutLinkSearchText || !this.checkoutLinkSearchText.trim()) {
      return this.availableCheckoutLinks;
    }
    const q = this.checkoutLinkSearchText.toLowerCase().trim();
    return this.availableCheckoutLinks.filter(
      (link) =>
        (link.title && link.title.toLowerCase().includes(q)) ||
        (link.url && link.url.toLowerCase().includes(q)) ||
        (link.displayTitle && link.displayTitle.toLowerCase().includes(q))
    );
  }

  getSelectedExpressCheckoutDisplay(): string {
    if (
      !this.selectedExpressCheckoutLink ||
      this.selectedExpressCheckoutLink?.value === 'ALL'
    ) {
      return 'All Orders';
    }
    if (this.selectedExpressCheckoutLink?.value === 'EXPRESS_ALL') {
      return 'Express Checkout Orders';
    }
    const selected = this.selectedExpressCheckoutLink;
    if (selected?.title) {
      return selected.title.startsWith('(') ? selected.title : `(${selected.title})`;
    }
    return selected?.display || 'All Orders';
  }

  getFilterGroup() {
    const labelFilters = (pick: (item) => string, display = (v) => v) =>
      [
        'ALL',
        ...[...new Set(this.OrderHistoryList.map(pick))].filter(Boolean).sort(),
      ].map((item) => ({
        display: item === 'ALL' ? 'View All' : display(item),
        value: item,
      }));
    // Older API has no split labels; fall back to the legacy status values.
    const hasLabels = this.OrderHistoryList.some((item) => item.order_status_label);
    this.statusFilters = hasLabels
      ? labelFilters((item) => item.order_status_label)
      : labelFilters((item) => item.status, (v) => this.getStatus(v)?.display ?? v);
    this.paymentStatusFilters = labelFilters((item) => item.payment_status_label);
    const paymentFilters = [
      'ALL',
      ...[
        ...new Set(this.OrderHistoryList.map((item) => item.payment_type)),
      ].sort(),
    ];
    this.paymentTypeFilters = paymentFilters.map((item) => ({
      display: this.getPaymentType(item)?.display,
      value: item,
    }));

    const platformFilters = [
      'ALL',
      ...[
        ...new Set(this.OrderHistoryList.map((item) => item.platform)),
      ].sort(),
    ];
    this.platformTypeFilters = platformFilters.map((item) => ({
      display: this.getPlatformType(item)?.display,
      value: item,
    }));

    const courFilters = [
      'ALL',
      ...[
        ...new Set(
          this.OrderHistoryList.map((item) => this.getCourierName(item))
        ),
      ].sort(),
    ];
    this.courierFilters = courFilters.map((item: string) => ({
      display: item,
      value: item,
    }));
  }

  searchOrder(event) {
    if (event.target.value === '') {
      this.paginationPage = 1;
      this.getOrderHistoryList();
    } else {
      this.paginationPage = 1;
      this.id = event.target.value;
      this.dataIsLoading = true;
      this.getSearchOrderHistory();
    }
  }

  clearOrderSearch() {
    this.orderId = '';
    this.id = '';
    this.paginationPage = 1;
    this.getOrderHistoryList();
  }

  getSearchOrderHistory() {
    this.orders.type = null;
    this.page = 1;
    this.endDate = this.startDate = this.currentDate;
    this.o_id = this.id;
    this.orderId = this.id;

    this.selectedStatus = this.resetDefaultOption();
    this.selectedPaymentStatus = this.resetDefaultOption();
    this.selectedCour = this.resetDefaultOption();
    this.selectedPaymentType = this.resetDefaultOption();
    this.selectedOrderType = this.resetDefaultOption();
    this.selectedOrderStatus = this.resetDefaultOption();
    this.selectedPlatformType = this.resetDefaultOption();
    this.selectedPendingOrder = this.resetDefaultOption();
    this.selectedExpressCheckoutLink = { display: 'All Orders', value: 'ALL' };
    this.svc.getSearchOrderHistory(this.bearer, this.o_id).subscribe((res) => {
      this.OrderHistoryList = this.allOrderList = res['data']['data'];
      if (this.OrderHistoryList && this.OrderHistoryList.length > 0) {
        this.OrderHistoryList.forEach((value, index) => {
          this.expanded[index] = false;
        });
      }
      this.orderPaginationList = res['data']['pagination'];

      this.page = this.orderPaginationList.page;
      this.totalPage = this.orderPaginationList.total_page;
      this.totalRecord = this.orderPaginationList.total_record;

      this.start = Math.round((this.page - 1) * this.pageSize);
      this.end = this.start + this.pageSize;
      this.end =
        this.end > this.orderPaginationList.total_record
          ? this.orderPaginationList.total_record
          : this.end;
      this.end = this.end > this.start ? this.end : this.start;
      // this.orderList = this.OrderHistoryList.slice(this.start, this.end);
      this.getFilterGroup();
      this.populateCheckoutLinksFromOrders();
      this.dataIsLoading = false;
    });
  }

  resetDefaultOption() {
    return { display: 'All', value: 'ALL' };
  }

  openModal(content, index) {
    this.selectedOrderDetailsTab = 0;
    this.selectedOrderHistory = this.OrderHistoryList[index];
    this.setMixtureShowAllArray(this.OrderHistoryList[index]);

    if (
      typeof this.selectedOrderHistory?.referral_code !== 'undefined' &&
      (this.selectedOrderHistory?.referral_code !== '' ||
        this.selectedOrderHistory?.referral_code !== null)
    ) {
      this.orderReferralExist = true;
    }
    this.orderReferralExist =
      typeof this.selectedOrderHistory?.referral_code !== 'undefined' &&
      (this.selectedOrderHistory?.referral_code !== '' ||
        this.selectedOrderHistory?.referral_code !== null);

    this.orderReferralLinkExist =
      typeof this.selectedOrderHistory?.referral_from_url !== 'undefined' &&
      (this.selectedOrderHistory?.referral_from_url !== '' ||
        this.selectedOrderHistory?.referral_from_url !== null);

    this.modalService
      .open(content, {
        ariaLabelledBy: 'modal-basic-title',
        size: 'lg',
        windowClass: 'modal-order-details-container',
        centered: true,
        backdrop: 'static',
      })
      .result.then(
        (result) => {
          this.closeResult = `Closed with: ${result}`;
        },
        (reason) => {
          this.closeResult = `Dismissed ${this.getDismissReason(reason)}`;
        }
      );
  }

  openFilter(content) {
    this.modalService
      .open(content, {
        windowClass: 'modal-filter-container',
        ariaLabelledBy: 'modal-basic-title',
        backdropClass: 'modal-filter-backdrop',
        centered: true,
        animation: false,
      })
      .result.then(
        (result) => {
          this.closeResult = `Closed with: ${result}`;
        },
        (reason) => {
          this.closeResult = `Dismissed ${this.getDismissReason(reason)}`;
        }
      );
  }

  private getDismissReason(reason: any): string {
    if (reason === ModalDismissReasons.ESC) {
      return 'by pressing ESC';
    } else if (reason === ModalDismissReasons.BACKDROP_CLICK) {
      return 'by clicking on a backdrop';
    } else {
      return `with: ${reason}`;
    }
  }

  get isConverted() {
    let info = JSON.parse(localStorage.getItem('5s-info'));
    this.dealerStatus = DEALER_STATUS[info.status].status;
    return this.dealerStatus === 'Converted';
  }

  convertedDealer() {
    this.loading = true;
    this.utils.notify.showError(
      'Order status updates disabled for CONVERTED accounts.'
    );
    setTimeout(() => {
      this.loading = false;
    }, 2000);
  }

  get filteredOrderHistoryList() {
    const selectedFilters = {};
    const filterTable = this.filterTable;

    Object.values(filterTable).forEach(({ filterName, filterCallback }) => {
      const value = this[filterName]?.value;
      if (value !== undefined && value !== 'ALL' && value !== '') {
        selectedFilters[filterName] = value;
        this.totalRecord = this.filteredOrderList.length;
      }
    });

    this.storeOrderList = this.allOrderList;
    this.filteredOrderList = this.storeOrderList;
    // this.totalRecord = this.filteredOrderList.length;
    if (this.dataIsLoading === false) {
      this.totalRecord = this.allOrderList?.length;
      this.sliceRecord();
    }

    if (Object.keys(selectedFilters).length === 0) {
      return this.OrderHistoryList;
    } else {
      Object.keys(selectedFilters).forEach((filterKey) => {
        const filterValue = selectedFilters[filterKey];
        const filterData = this.getFilterDataByName(filterKey);

        this.filteredOrderList = this.filteredOrderList.filter((item) =>
          filterData?.filterCallback(item, filterValue)
        );
      });
      this.slicePaginationNumber();
      return this.filteredOrderList;
    }
  }

  slicePaginationNumber() {
    this.start = Math.round(
      (this.paginationPage - 1) * this.paginationPageSize
    );
    this.end = this.start + this.paginationPageSize;
    this.end =
      this.end > this.filteredOrderList.length
        ? this.filteredOrderList.length
        : this.end;
    if (this.end < this.start) {
      this.start = 0;
    }
    this.end = this.end > this.start ? this.end : this.start;

    this.filteredOrderList.sort((a, b) => b.created.localeCompare(a.created));
    // this.paginationPage = this.paginationPage !== 1 ? 1 : this.paginationPage;
    this.totalRecord = this.filteredOrderList.length;

    this.orderList = this.filteredOrderList.slice(this.start, this.end);
    if (this.orderList.length > 0) {
      this.orderList.forEach((value, index) => {
        this.expanded[index] = false;
      });
    }
    this.filteredOrderList = this.orderList;
    this.OrderHistoryList = this.filteredOrderList;
  }

  getFilterDataByName(filterName) {
    return this.filterTable.find((filter) => filter?.filterName === filterName);
  }

  isSubscription(orderData) {
    return +orderData.order_type === 4;
  }

  isExpressCheckout(orderData) {
    return +orderData.order_type === 3;
  }

  changeDetailsTab(tabIndex) {
    this.selectedOrderDetailsTab = tabIndex;
  }

  isCurrentDetailsTab(tabIndex) {
    return +this.selectedOrderDetailsTab === +tabIndex;
  }

  isInDateRange(orderData) {
    if (orderData?.next_renewal == '') return true;
    const nextRenewal = `${orderData.next_renewal} 24:00:00`;
    const startDate = new Date(orderData.order_date);
    const endDate = new Date(nextRenewal);

    const dateToCheck = new Date();
    console.log({ startDate, endDate, dateToCheck });

    return dateToCheck >= startDate && dateToCheck <= endDate;
  }

  onDateSelection(date: NgbDate) {
    if (!this.fromDate && !this.toDate) {
      this.fromDate = date;
    } else if (this.fromDate && !this.toDate && date.after(this.fromDate)) {
      this.toDate = date;
    } else {
      this.toDate = null;
      this.fromDate = date;
    }
  }

  clearDateRange() {
    this.fromDate = null;
    this.toDate = null;
  }

  isHovered(date: NgbDate) {
    return (
      this.fromDate &&
      !this.toDate &&
      this.hoveredDate &&
      date.after(this.fromDate) &&
      date.before(this.hoveredDate)
    );
  }

  isInside(date: NgbDate) {
    return this.toDate && date.after(this.fromDate) && date.before(this.toDate);
  }

  isRange(date: NgbDate) {
    return (
      date.equals(this.fromDate) ||
      (this.toDate && date.equals(this.toDate)) ||
      this.isInside(date) ||
      this.isHovered(date)
    );
  }

  datePeriod() {
    this.DATE_FILTERS.forEach((item) => {
      item.selectable = false;
    });

    if (this.dateFilter) {
      this.dateFilter.selectable = true;
    }

    let start = moment();
    let end = moment();

    if (this.dateFilter?.value === 'today') {
      start = moment(`${this.day}/${this.month + 1}/${this.year}`, 'DD/MM/YYYY');
      end = moment(`${this.day}/${this.month + 1}/${this.year}`, 'DD/MM/YYYY');
    } else if (this.dateFilter?.value === 'yesterday') {
      const yesterday = moment().subtract(1, 'days');
      start = yesterday.clone();
      end = yesterday.clone();
    } else if (this.dateFilter?.value === 'cm') {
      start = moment(`01/${this.month + 1}/${this.year}`, 'DD/MM/YYYY');
      end = moment(`${this.day}/${this.month + 1}/${this.year}`, 'DD/MM/YYYY');
    } else if (this.dateFilter?.value === 'lm') {
      const lastMonth = moment().subtract(1, 'month');
      start = lastMonth.clone().startOf('month');
      end = lastMonth.clone().endOf('month');
    } else if (this.dateFilter?.value === 'last30') {
      end = moment(`${this.day}/${this.month + 1}/${this.year}`, 'DD/MM/YYYY');
      start = end.clone().subtract(30, 'days');
    } else {
      // Default to Current Month
      start = moment(`01/${this.month + 1}/${this.year}`, 'DD/MM/YYYY');
      end = moment(`${this.day}/${this.month + 1}/${this.year}`, 'DD/MM/YYYY');
    }

    if (start && end) {
      const rangeVal = `${start.format('DD-MMM-YYYY')} to ${end.format(
        'DD-MMM-YYYY'
      )}`;
      this.orders.type = {
        value: { start: start, end: end },
        display: this.dateFilter?.display || rangeVal,
        range: true,
      };
      this.startDate = `${start.format('YYYY-MM-DD')}`;
      this.endDate = `${end.format('YYYY-MM-DD')}`;
    }
    this.page = 1;

    this.getOrderHistoryList();
  }

  setDueDate() {
    this.dateFilter = null;
    const start = moment(
      `${this.fromDate.day}/${this.fromDate.month}/${this.fromDate.year}`,
      'DD/MM/YYYY'
    );
    const end = moment(
      `${this.toDate.day}/${this.toDate.month}/${this.toDate.year}`,
      'DD/MM/YYYY'
    );

    if (start && end) {
      const rangeVal = `${start.format('DD-MMM-YYYY')} to ${end.format(
        'DD-MMM-YYYY'
      )}`;
      this.orders.type = {
        value: { start: start, end: end },
        display: rangeVal,
        range: true,
      };
      this.startDate = `${start.format('YYYY-MM-DD')}`;
      this.endDate = `${end.format('YYYY-MM-DD')}`;
    }
    this.getOrderHistoryList();
  }

  getStatus(key) {
    return STATUS[key?.toUpperCase()];
  }

  getOrderStatusClass(label: string) {
    return ORDER_STATUS_BADGE[label] ?? 'badge-secondary';
  }

  getPaymentStatusClass(label: string) {
    return PAYMENT_STATUS_BADGE[label] ?? 'badge-secondary';
  }

  getOrderStatusHint(label: string): string {
    return this.utils.translate(ORDER_STATUS_HINT[label] ?? 'Order progress');
  }

  getPaymentStatusHint(label: string): string {
    return this.utils.translate(PAYMENT_STATUS_HINT[label] ?? 'Payment');
  }

  getOrderType(key) {
    if (key === 'membership') return ORDER_TYPE['MEMBERSHIP'];
    if (key === 'openapi') return ORDER_TYPE['OPEN_API'];
    if (key === 'fiv5s') return ORDER_TYPE['FIV5S'];
    return ORDER_TYPE['DEFAULT'];
  }

  getPaymentType(key) {
    return PAYMENT[key?.toUpperCase()];
  }

  private prepareOrders(which, list, updater) {
    which['raw'] = list;

    const today = moment(moment().format('YYYY-MM-DD'), 'YYYY-MM-DD');

    list.map((item) => {
      const cDate = moment(item.cDate, 'YYYY-MM-DD');

      if (today.isSame(cDate)) {
        updater(which.today, item);
      }

      if (today.diff(cDate, 'days') == 1) {
        updater(which.yesterday, item);
      }

      if (today.month() - cDate.month() == 1) {
        updater(which.lm, item);
      }

      if (today.month() === cDate.month()) {
        updater(which.cm, item);
      }

      if (today.year() === cDate.year()) {
        updater(which.cy, item);
      }
    });
  }

  getCourierName(item) {
    const shippingOption = item.shippingOption;
    if (shippingOption) {
      const shipMethod = shippingOption.shippingMethodName.split(';');

      return shipMethod.length > 1 ? shipMethod[1] : shipMethod[0];
    }

    return '-';
  }

  viewOnFiv5() {
    window.open(this.partnerPlatformUrl + '#/all-order', '_blank');
  }

  get list() {
    let filtered = [];
    if (!this.orders.type) {
      filtered = this.orders.raw;
    } else if (this.orders.type.range) {
      const range = this.orders.type.value;
      const startD = moment(range['start'], 'MMMM DD, YYYY');
      const endD = moment(range['end'], 'MMMM DD, YYYY');
      filtered = this.orders.raw.filter((item) => {
        const dDate = moment(item.createDate, 'MMMM DD, YYYY');
        return dDate.isSameOrAfter(startD) && dDate.isSameOrBefore(endD);
      });
    } else {
      filtered = this.orders[this.orders.type.value];
    }

    const statVal = (this.selectedStatus && this.selectedStatus.value) || 'ALL';
    const courVal = (this.selectedCour && this.selectedCour.value) || 'ALL';
    const paymentVal =
      (this.selectedPaymentType && this.selectedPaymentType.value) || 'ALL';

    if (statVal !== 'ALL') {
      filtered = filtered.filter((item) => item.status == statVal);
    }

    if (courVal !== 'ALL') {
      filtered = filtered.filter(
        (item) => this.getCourierName(item) == courVal
      );
    }

    if (paymentVal !== 'ALL') {
      filtered = filtered.filter((item) => item.payment_type == paymentVal);
    }

    this.withPagination = filtered.length > this.pageSize;
    return this.sorted(filtered);
  }

  sliceRecord() {
    this.start = Math.round(
      (this.paginationPage - 1) * this.paginationPageSize
    );
    this.end = this.start + this.paginationPageSize;
    this.end =
      this.end > this.storeOrderList?.length
        ? this.storeOrderList?.length
        : this.end;
    this.end = this.end > this.start ? this.end : this.start;

    // Sort listing by created_at date
    this.storeOrderList?.sort((a, b) => b.created.localeCompare(a.created));

    this.orderList = this.storeOrderList?.slice(this.start, this.end);
    if (this.orderList?.length > 0) {
      this.orderList?.forEach((value, index) => {
        this.expanded[index] = false;
      });
    }
    this.OrderHistoryList = this.orderList;
  }

  changePage(page: any) {
    this.paginationPage = page;
    let pageInt = Number(this.page);
    let a = pageInt * this.limit;
    let b = this.paginationPage * this.paginationPageSize;
    if (a - b >= 0 && a - b < this.limit) {
      let filterTable = this.filterTable;
      Object.values(filterTable).forEach(({ filterName, filterCallback }) => {
        const value = this[filterName]?.value;
        if (value !== undefined && value !== 'ALL' && value !== '') {
          this.slicePaginationNumber();
        } else if (value === 'ALL') {
          this.sliceRecord();
        }
      });
    }
    // else{
    //   if(a-b < this.limit){
    //     this.page = pageInt +1;
    //     this.getOrderHistoryList();
    //   }else{
    //     this.page = pageInt -1;
    //     this.getOrderHistoryList();
    //   }
    // }
  }

  selectPage(pageOfItems) {
    this.paginationPageSize = pageOfItems;
    this.paginationPage = 1;
    // this.getOrderHistoryList();
  }

  editOrderPage(data) {
    this.codData = JSON.stringify(data);
    localStorage.setItem('codData', this.codData);
  }

  getCheckoutUrl({ country_code, language_code, url }) {
    const urlSegments = [];
    urlSegments.push(this.store_url);
    if (country_code) urlSegments.push(country_code?.toLowerCase());
    if (language_code) urlSegments.push(language_code?.toLowerCase());
    urlSegments.push('express');
    urlSegments.push(url);
    return urlSegments.join('/');
  }

  emptyCheckoutLinkDetails(checkoutLinkDetails) {
    return !Object.keys(checkoutLinkDetails ?? []).length;
  }

  private getPlatformType(key: any): any {
    return PLATFORM[key?.toUpperCase()];
  }

  public setSelectedCodStatus(status: string, orderId: string): void {
    this.selectedCodStatus[orderId] = status;
  }

  public getSelectedCodStatus(orderId: any): boolean {
    return typeof this.selectedCodStatus[orderId] !== 'undefined';
  }

  public approveCod(data: any): void {
    this.loading = true;
    const body = {
      store_order_id: data.store_order_id,
      product_items: data.input.product_items,
    };

    this.svc.updateCodStatus(body, this.bearer).subscribe(
      (res) => {
        const msg = res['data'];
        if (res['status_code'] === 400 || res['status_code'] === 'error') {
          if (msg?.fiv5s_api_error !== undefined) {
            this.utils.notify.showError(res['data']['fiv5s_api_error']);
          } else {
            this.utils.notify.showError(res['message']);
          }

          setTimeout(() => {
            this.loading = false;
          }, 3000);
        } else {
          this.utils.notify.showSuccess(
            this.utils.translate('Updated Successfully!')
          );
          setTimeout(() => {
            this.getOrderHistoryList();
            // this.loading = false;
          }, 3000);
        }
      },
      (error) => {
        if (
          error['error']['status_code'] === 400 ||
          error['error']['status_code'] === 'error'
        ) {
          if (error?.data?.fiv5s_api_error !== undefined) {
            this.utils.notify.showError(error['data']['fiv5s_api_error']);
          } else {
            if (error?.['error']?.['message']) {
              if (typeof error['error']['message'] === 'string') {
                this.utils.notify.showError(error['error']['message']);
              } else {
                let msg = '';
                if (error?.['error']?.['message']?.['error']) {
                  msg = error['message']['error'];
                } else {
                  Object.keys(error['error']['message']).forEach((item) => {
                    msg += error['error']['message'][item] + ' ';
                  });
                }
                this.utils.notify.showError(msg);
              }
            } else {
              this.utils.notify.showError(error['message']);
            }
          }
          setTimeout(() => {
            this.loading = false;
          }, 3000);
        } else {
          this.utils.notify.showError(error['message']);
          setTimeout(() => {
            this.loading = false;
          }, 3000);
        }
      }
    );
  }

  public rejectCod(data: any, template: any): void {
    this.modalService
      .open(template, {
        ariaLabelledBy: 'modal-basic-title',
        size: 'md',
        windowClass: 'modal-reject-cod',
        centered: true,
        backdrop: 'static',
      })
      .result.then(
        (result) => {
          if (result === 'submit') {
            this.loading = true;
            const body = {
              store_order_id: data.store_order_id,
              product_items: data.input.product_items,
              remark: this.remark.trim(),
            };

            this.svc.cancelCodStatus(body, this.bearer).subscribe(
              (res) => {
                this.utils.notify.showSuccess(
                  this.utils.translate('Updated Successfully!')
                );
                setTimeout(() => {
                  this.getOrderHistoryList();
                  // this.loading = false;
                }, 3000);
              },
              (error) => {
                if (
                  error['error']['status_code'] === 400 ||
                  error['error']['status_code'] === 'error'
                ) {
                  this.utils.notify.showError(error['message']);
                  this.loading = false;
                }
              }
            );
          }
          this.closeResult = `Closed with: ${result}`;
          this.remark = '';
        },
        (reason) => {
          this.closeResult = `Dismissed ${this.getDismissReason(reason)}`;
          this.remark = '';
        }
      );
  }

  public onUpdateClick(modal: NgbModalRef): void {
    if (!this.remark || this.remark.trim() === '') {
      this.remarkError = true;
      return; // Exit function without closing the modal
    }
    modal.close('submit'); // Close the modal if remark is provided
  }

  @HostListener('window:resize', ['$event'])
  public onResize(event: any): void {
    this.currentClass = this.getClassBasedOnWidth();
  }

  public getClassBasedOnWidth(): string {
    if (window.innerWidth >= 350 && window.innerWidth < 761) {
      return 'col-md-2';
    } else if (window.innerWidth >= 340) {
      return 'col-sm-2';
    } else {
      return 'col-lg-2';
    }
  }

  public getWindowType(): string {
    if (window.innerWidth >= 560 && window.innerWidth < 761) {
      return 'tablet';
    } else if (window.innerWidth >= 340 && window.innerWidth < 560) {
      return 'mobile';
    } else {
      return 'screen';
    }
  }

  public setMixtureShowAllArray(items: any): void {
    const orderDetails = items.order_details;

    for (const item of orderDetails) {
      const productId = item.data.p_id;
      this.selectedShowAll[productId] = true;
      this.selectedShowAllText[productId] = this.utils.translate('Hide All');
    }
  }

  // Subscription order items leave mixture_product.data.product_name empty — the
  // real names live under subscription.cycles[].products[].mixture_product[]. Fall
  // back to that lookup when the direct field is blank.
  public resolveMixtureName(item: any, mp: any): string {
    const direct = mp?.data?.product_name;
    const name = direct || this.lookupSubscriptionMixtureName(item, +mp?.data?.p_id);
    // Backend-supplied names like "MY-MOVEON\V1" use backslash as a variant separator;
    // render them with a space for readability.
    return (name || '').replace(/\\/g, ' ');
  }

  private lookupSubscriptionMixtureName(item: any, pid: number): string {
    if (+item?.data?.is_subscription !== 1 || !pid) return '';
    const cycleProducts = item.data.subscription?.cycles?.[0]?.products || [];
    for (const cp of cycleProducts) {
      const match = (cp.mixture_product || []).find((smp: any) => +smp.p_id === pid);
      if (match?.product_name) return match.product_name;
    }
    return '';
  }

  public toggleMixture(productId: any): void {
    const toggled = !this.selectedShowAll[productId];
    this.selectedShowAll[productId] = toggled;

    if (toggled === false) {
      this.selectedShowAllText[productId] = this.utils.translate('Show All');
    } else {
      this.selectedShowAllText[productId] = this.utils.translate('Hide All');
    }
  }

  public getProgramInfo(storeId: any): void {
    this.svc.getProgramInfo(this.bearer, storeId).subscribe(
      (res: any) => {
        this.isReferralExist = res['data']['program'] !== null;
      },
      (error) => {
        console.log({ error });
      }
    );
  }

  public getDealerInfo(): void {
    this.svc.getDealerInfo(this.bearer).subscribe(
      (res: any) => {
        this.dealerInfo = res['data'];
      },
      (error) => {
        console.log({ error });
      }
    );
  }

  get isCompanyStore() {
    return (
      this.dealerInfo?.company_store ||
      this.dealerInfo?.dealer_code?.toLowerCase().startsWith('u')
    );
  }

  // Order-status tabs above the list: each status in the loaded orders, in
  // order-lifecycle order, with how many orders it holds under the other
  // active filters (so a tab's count matches the list it shows).
  private statusTabCache: { key: string; source: any[]; tabs: any[] } = {
    key: '',
    source: null,
    tabs: [],
  };

  get statusTabs(): { option: any; count: number }[] {
    const source = this.allOrderList || [];
    const others = this.filterTable
      .filter(({ filterName }) => filterName !== 'selectedStatus')
      .map((filter) => ({ ...filter, value: this[filter.filterName]?.value }))
      .filter(({ value }) => value !== undefined && value !== 'ALL' && value !== '');
    const key = [
      source.length,
      this.statusFilters.map((option) => option.value).join('|'),
      others.map(({ filterName, value }) => `${filterName}=${value}`).join('|'),
    ].join('#');
    if (key === this.statusTabCache.key && source === this.statusTabCache.source) {
      return this.statusTabCache.tabs;
    }

    const scoped = source.filter((order) =>
      others.every(({ filterCallback, value }) => filterCallback(order, value))
    );
    const lifecycle = Object.keys(ORDER_STATUS_BADGE);
    const rank = (value) =>
      lifecycle.includes(value) ? lifecycle.indexOf(value) : lifecycle.length;
    const statuses = this.statusFilters
      .filter((option) => option.value !== 'ALL')
      .sort((a, b) => rank(a.value) - rank(b.value));
    const tabs = [
      {
        option: this.statusFilters.find((option) => option.value === 'ALL') ?? {
          display: 'View All',
          value: 'ALL',
        },
        count: scoped.length,
      },
      ...statuses.map((option) => ({
        option,
        count: scoped.filter(
          (order) => (order?.order_status_label ?? order?.status) === option.value
        ).length,
      })),
    ];

    this.statusTabCache = { key, source, tabs };
    return tabs;
  }

  selectStatusTab(option: any) {
    this.selectedStatus = option;
    this.paginationPage = 1;
  }

  // "More filters" holds payment status, payment method and platform; a group
  // shows when it has a real choice (or is in use).
  showFilterGroup(options: any[], selected: any): boolean {
    return options?.length > 2 || (selected?.value && selected.value !== 'ALL');
  }

  get hasMoreFilters(): boolean {
    return (
      this.showFilterGroup(this.paymentStatusFilters, this.selectedPaymentStatus) ||
      this.showFilterGroup(this.paymentTypeFilters, this.selectedPaymentType) ||
      this.showFilterGroup(this.platformTypeFilters, this.selectedPlatformType)
    );
  }

  get moreFilterCount(): number {
    return [this.selectedPaymentStatus, this.selectedPaymentType, this.selectedPlatformType].filter(
      (f) => f?.value !== undefined && f.value !== 'ALL' && f.value !== ''
    ).length;
  }

  // Phones fold the toolbar filters behind a "Filters" button next to the
  // search; its count says how many of them are narrowing the list.
  toolbarOpen = false;

  get toolbarFilterCount(): number {
    const dateChanged = this.dateFilter?.value !== 'cm' || (this.fromDate && this.toDate);
    const linkChanged =
      this.selectedExpressCheckoutLink?.value && this.selectedExpressCheckoutLink.value !== 'ALL';
    return (dateChanged ? 1 : 0) + (linkChanged ? 1 : 0) + this.moreFilterCount;
  }

  isSelectedFilter(selected: any, option: any): boolean {
    return (selected?.value || 'ALL') === option?.value;
  }

  clearMoreFilters() {
    this.selectedPaymentStatus = this.paymentStatusFilters[0];
    this.selectedPaymentType = this.paymentTypeFilters[0];
    this.selectedPlatformType = this.platformTypeFilters[0];
    this.paginationPage = 1;
  }

  isPendingApproval(data: any): boolean {
    return +data?.payment_status === 2;
  }

  // Orders in the loaded date range that wait for approval, by the same rule
  // as the pending-order filter (pendingOrderCounter only sees the current page).
  get pendingApprovalCount(): number {
    return (this.allOrderList || []).filter((order) =>
      ['COD APPROVAL', 'SELF COLLECT'].includes(order?.status)
    ).length;
  }

  // Of those, the ones placed more than 48 hours ago. The browser records the
  // Meta Purchase at checkout and the server only at approval; Meta merges the
  // two only within ~48h, so a later approval can count the sale twice.
  get overdueApprovalCount(): number {
    const cutoff = Date.now() - 48 * 60 * 60 * 1000;
    return (this.allOrderList || []).filter((order) => {
      if (!['COD APPROVAL', 'SELF COLLECT'].includes(order?.status)) return false;
      const created = Date.parse(String(order?.created || '').replace(' ', 'T'));
      return !isNaN(created) && created < cutoff;
    }).length;
  }

  // A click anywhere on an order row opens it, unless text was being selected to copy.
  openOrderFromRow(content: any, index: number) {
    if (window.getSelection()?.toString()) return;
    this.openModal(content, index);
  }

  // Approve from the list: confirm first, then the same approval as before.
  confirmApproveCod(data: any): void {
    const escape = (value: any) =>
      String(value ?? '').replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
    const total = `${data?.currency ?? ''} ${Number(data?.total_price ?? 0).toLocaleString('en-US', {
      maximumFractionDigits: 3,
    })}`.trim();

    this.utils.msgBox.open({
      title: this.utils.translate('Approve this order?'),
      message: this.utils.translate('Order #{0} ({1}) will be sent for fulfilment.', [
        escape(this.getOrderId(data)),
        escape(total),
      ]),
      icon: 'question',
      buttonList: [
        { text: this.utils.translate('Cancel') },
        {
          text: this.utils.translate('Approve'),
          style: 'success',
          onClick: () => this.approveCod(data),
        },
      ],
    });
  }

  selectPendingOrder() {
    if (!this.pendingOrderCloseBtn) {
      this.dateStart();
      this.pendingOrderCloseBtn = true;
      this.selectedPendingOrder = { display: 'All', value: 'pendingOrder' };
      this.pendingOrderCounter = this.pendingOrder.length;
      this.paginationPage = 1;
    }
  }

  resetPendingOrder() {
    this.datePeriod();
    this.pendingOrderCloseBtn = false;
    this.selectedPendingOrder = this.resetDefaultOption();
    this.paginationPage = 1;
  }

  dateStart() {
    let end = moment(
      `${this.day}/${this.month + 1}/${this.year}`,
      'DD/MM/YYYY'
    );
    let start = moment(
      `${this.minDate.day}/${this.minDate.month}/${this.minDate.year}`,
      'DD/MM/YYYY'
    );

    if (start && end) {
      const rangeVal = `${start.format('DD-MMM-YYYY')} to ${end.format(
        'DD-MMM-YYYY'
      )}`;
      this.orders.type = {
        value: { start: start, end: end },
        display: rangeVal,
        range: true,
      };
      this.startDate = `${start.format('YYYY-MM-DD')}`;
      this.endDate = `${end.format('YYYY-MM-DD')}`;
    }
    this.page = 1;

    this.getOrderHistoryList();
  }

  checkPendingOrder() {
    this.dataIsLoading = true;
    this.pendingOrder = [];

    if (this.OrderHistoryList && this.OrderHistoryList.length > 0) {
      this.OrderHistoryList.forEach((value, index) => {
        this.expanded[index] = false;
        const isPendingOrder =
          value?.status === 'COD APPROVAL' || value?.status === 'SELF COLLECT';
        if (isPendingOrder) {
          this.pendingOrder.push(value);
        }
        value.status =
          value.payment_status === '8' && value.payment_type === 'self_collect'
            ? 'CANCEL SELF COLLECT'
            : value.status;
      });
    }
    this.pendingOrderCounter = this.pendingOrder.length;
    this.dataIsLoading = false;
    if (this.pendingOrderCounter > 0) {
      this.pendingOrderDisplay = true;
    }
    if (this.loading) {
      this.loading = false;
    }
  }
}
